import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { RealtimeEnvelope } from "@/types/realtime.type";
import { UNIVERSE_REALTIME } from "./universeRealtime";
import { WORKSHOP_REALTIME } from "../workshop/api/workshopRealtime";
import { CALENDAR_REALTIME } from "../content-calendar/api/calendarRealtime";
import { SIGNALS_REALTIME } from "../market-signals/api/signalsRealtime";
import { STRATEGY_REALTIME } from "../strategy/api/strategyRealtime";

/**
 * One entry per page that has moved from polling to realtime. Handlers run
 * for every event whatever page is open: invalidating a query nobody is
 * watching only marks it stale, so the next page to mount it refetches.
 */
export type RealtimeHandler = {
  /** Applies one event to the cache. Ignores events it does not own. */
  apply: (qc: QueryClient, event: RealtimeEnvelope) => void;
  /** Reads that may have missed events while the brand was not live. */
  resyncKeys: (brandId: string) => QueryKey[];
};

const HANDLERS: RealtimeHandler[] = [
  UNIVERSE_REALTIME,
  WORKSHOP_REALTIME,
  CALENDAR_REALTIME,
  SIGNALS_REALTIME,
  STRATEGY_REALTIME,
];

export function applyRealtimeEvent(qc: QueryClient, event: RealtimeEnvelope) {
  if (event.type === "brand.updated") {
    // Name and logo are read from the org's brand list.
    void qc.invalidateQueries({ queryKey: ["brands"] });
  }
  for (const handler of HANDLERS) handler.apply(qc, event);
}

/**
 * Called when a brand's room is (re)joined. Events are not replayed, so
 * anything fetched before the join may be stale. Queries never fetched yet
 * are left to their own first load.
 */
export function resyncBrand(qc: QueryClient, brandId: string, since: number) {
  const keys: QueryKey[] = [["brands"], ...HANDLERS.flatMap((h) => h.resyncKeys(brandId))];
  for (const queryKey of keys) {
    void qc.invalidateQueries({
      queryKey,
      predicate: (query) =>
        query.state.dataUpdatedAt > 0 && query.state.dataUpdatedAt < since,
    });
  }
}
